import React, { useEffect, useState, useCallback } from "react";
import { Title, Meta, Link as HeadLink } from "react-head";
import { motion } from "motion/react";
import { useParams, useNavigate } from "react-router-dom";
import { FiArrowLeft, FiDownload, FiEye, FiLayers, FiImage } from "react-icons/fi";
import { HiSparkles } from "react-icons/hi2";

import ClientCalling from "../api/ClientCalling";
import WallpaperCatBar from "../components/Client/WallpaperCatBar";
import WallpaperGrid from "../components/Client/WallpaperGrid";
import ClientAdSense from "../components/Client/ClientAdSense";
import FixedButton from "../components/Client/FixedButton";
import WallpaperFooter from "../components/Client/WallpaperFooter";
import SmartImage from "../components/Client/SmartImage";
import { formatView } from "../components/formatView";

const ClientWallpaperView = () => {
    const { slug } = useParams();
    const navigate = useNavigate();

    const [wallpaper, setWallpaper] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [downloading, setDownloading] = useState(false);

    /* fetch detail (records a view) */
    useEffect(() => {
        let active = true;
        (async () => {
            setLoading(true);
            setNotFound(false);
            setWallpaper(null);
            window.scrollTo({ top: 0, behavior: "auto" });

            try {
                const resp = await ClientCalling.getWallpaperBySlugClient(slug);
                if (!active) return;

                if (resp?.status && resp.data) {
                    setWallpaper(resp.data);
                } else {
                    setNotFound(true);
                }
            } catch (e) {
                if (active) setNotFound(true);
            } finally {
                if (active) setLoading(false);
            }
        })();
        return () => {
            active = false;
        };
    }, [slug]);

    /* download (blob → fallback open) */
    const downloadImage = useCallback(async () => {
        const url = wallpaper?.w_image;
        if (!url) return;
        setDownloading(true);
        try {
            const res = await fetch(url, { mode: "cors" });
            if (!res.ok) throw new Error("network");
            const blob = await res.blob();
            const objUrl = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = objUrl;
            a.download = `${wallpaper.w_slug || "wallpaper"}.jpg`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(objUrl);
        } catch (e) {
            const a = document.createElement("a");
            a.href = url;
            a.target = "_blank";
            a.rel = "noopener";
            a.click();
        } finally {
            setDownloading(false);
        }
    }, [wallpaper]);

    const catSlug = wallpaper?.w_category?.w_slug || "";
    const catName = wallpaper?.w_category?.w_name || "";
    const heading = wallpaper?.w_name || "Wallpaper";

    return (
        <div className='min-h-screen dm-sans client-home-bg'>
            <Title>{heading} | PicPromptify Wallpapers</Title>
            <Meta
                name='description'
                content={`Download ${heading} — free HD wallpaper (1080×1920) on PicPromptify.`}
            />
            <Meta property='og:title' content={`${heading} | PicPromptify`} />
            <Meta property='og:type' content='website' />
            {wallpaper?.w_image && (
                <Meta property='og:image' content={wallpaper.w_image} />
            )}
            <HeadLink
                rel='canonical'
                href={`https://picpromptify.com/wallpaper/view/${slug}`}
            />

            {/* top scrollable category bar (current category active, no navbar) */}
            <WallpaperCatBar activeSlug={catSlug} />
            <FixedButton />

            <section className='relative w-full px-4 sm:px-6 md:px-8 pt-5 md:pt-7 pb-12 md:pb-20'>
                <div className='mx-auto max-w-6xl'>
                    {/* top banner ad */}
                    <ClientAdSense
                        slot='9729451986'
                        containerClassName='w-full mb-5 md:mb-7'
                    />

                    {/* back */}
                    <motion.button
                        whileHover={{ x: -3 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => navigate(-1)}
                        className='ios-glass-btn inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-client-font/85 text-[11px] md:text-xs font-medium mb-5 md:mb-7'
                    >
                        <FiArrowLeft className='text-sm' />
                        Back
                    </motion.button>

                    {/* ===== loading ===== */}
                    {loading && (
                        <div className='grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10'>
                            <div className='ios-shimmer w-full max-w-[340px] mx-auto md:mx-0 aspect-[9/16] rounded-3xl' />
                            <div className='space-y-4 pt-2'>
                                <div className='h-4 w-24 rounded-full ios-shimmer' />
                                <div className='h-8 w-2/3 rounded-md ios-shimmer' />
                                <div className='h-4 w-1/3 rounded-md ios-shimmer opacity-60' />
                                <div className='h-12 w-full max-w-xs rounded-2xl ios-shimmer mt-4' />
                            </div>
                        </div>
                    )}

                    {/* ===== not found ===== */}
                    {!loading && notFound && (
                        <motion.div
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            className='flex flex-col items-center justify-center text-center py-16 md:py-24'
                        >
                            <div
                                className='w-16 h-16 md:w-20 md:h-20 rounded-2xl flex items-center justify-center mb-5'
                                style={{
                                    background:
                                        "linear-gradient(135deg, rgba(127,0,255,0.25), rgba(252,250,2,0.08))",
                                    border: "1px solid rgba(255,255,255,0.12)",
                                }}
                            >
                                <FiImage className='text-2xl md:text-3xl text-client-title' />
                            </div>
                            <h3 className='text-client-font font-bold text-lg md:text-2xl tracking-tight'>
                                Wallpaper not found
                            </h3>
                            <p className='text-client-font/55 text-sm md:text-base mt-2 max-w-md'>
                                This wallpaper isn't available right now.
                            </p>
                            <motion.button
                                whileHover={{ y: -2, scale: 1.04 }}
                                whileTap={{ scale: 0.96 }}
                                onClick={() => navigate("/wallpaper")}
                                className='mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-white font-semibold text-xs md:text-sm'
                                style={{
                                    background:
                                        "linear-gradient(135deg, #7F00FF, #FF8AFF)",
                                    boxShadow:
                                        "0 12px 28px -8px rgba(127,0,255,0.55), inset 0 1px 0 rgba(255,255,255,0.25)",
                                }}
                            >
                                Browse Wallpapers
                            </motion.button>
                        </motion.div>
                    )}

                    {/* ===== detail ===== */}
                    {!loading && wallpaper && (
                        <div className='grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10 items-start'>
                            {/* image */}
                            <motion.div
                                initial={{ opacity: 0, y: 20, scale: 0.96 }}
                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                                className='w-full max-w-[340px] mx-auto md:mx-0'
                            >
                                <div
                                    className='relative w-full aspect-[9/16] rounded-3xl overflow-hidden'
                                    style={{
                                        border: "1px solid rgba(255,255,255,0.14)",
                                        boxShadow:
                                            "0 30px 70px -20px rgba(127,0,255,0.5), 0 14px 32px -10px rgba(0,0,0,0.7)",
                                    }}
                                >
                                    <SmartImage
                                        src={wallpaper.w_image}
                                        alt={wallpaper.w_name}
                                        eager
                                        className='absolute inset-0 w-full h-full object-cover'
                                    />
                                </div>
                            </motion.div>

                            {/* info */}
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{
                                    duration: 0.6,
                                    delay: 0.1,
                                    ease: [0.22, 1, 0.36, 1],
                                }}
                                className='pt-1 flex flex-col items-center text-center md:items-start md:text-left'
                            >
                                {catName && (
                                    <button
                                        onClick={() =>
                                            navigate(`/wallpaper/category/${catSlug}`)
                                        }
                                        className='inline-flex items-center gap-1.5 ios-glass px-2.5 py-1 rounded-full text-[10px] md:text-xs text-client-font/85 mb-3'
                                    >
                                        <FiLayers
                                            style={{ color: "#FCFA02", fontSize: 11 }}
                                        />
                                        <span className='tracking-wide font-semibold'>
                                            {catName}
                                        </span>
                                    </button>
                                )}

                                <h1 className='text-2xl md:text-4xl font-bold text-client-font tracking-tight leading-tight'>
                                    {wallpaper.w_name}
                                </h1>

                                <div className='mt-3 flex items-center justify-center md:justify-start gap-4 text-client-font/55 text-xs md:text-sm'>
                                    <span className='inline-flex items-center gap-1.5 tabular-nums'>
                                        <FiEye className='text-sm' />
                                        {formatView(wallpaper.w_view_count || 0)} views
                                    </span>
                                    <span className='inline-flex items-center gap-1.5'>
                                        <HiSparkles
                                            style={{ color: "#FCFA02", fontSize: 13 }}
                                        />
                                        1080 × 1920 HD
                                    </span>
                                </div>

                                <motion.button
                                    whileHover={{ y: -2, scale: 1.02 }}
                                    whileTap={{ scale: 0.97 }}
                                    onClick={downloadImage}
                                    disabled={downloading}
                                    className='mt-6 w-full max-w-xs inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-2xl text-white font-semibold text-sm disabled:opacity-70'
                                    style={{
                                        background:
                                            "linear-gradient(135deg, #7F00FF, #FF8AFF)",
                                        boxShadow:
                                            "0 14px 30px -10px rgba(127,0,255,0.6), inset 0 1px 0 rgba(255,255,255,0.25)",
                                    }}
                                >
                                    {downloading ? (
                                        <>
                                            <div className='w-4 h-4 rounded-full border-2 border-white/40 border-t-white animate-spin' />
                                            Downloading…
                                        </>
                                    ) : (
                                        <>
                                            <FiDownload className='text-base' />
                                            Download Wallpaper
                                        </>
                                    )}
                                </motion.button>

                                <p className='text-client-font/40 text-[11px] md:text-xs mt-3 max-w-xs'>
                                    Tap download to save this wallpaper in full
                                    1080×1920 quality to your device.
                                </p>
                            </motion.div>
                        </div>
                    )}

                    {/* ===== recommendations (same category, infinite scroll) ===== */}
                    {!loading && wallpaper && catSlug && (
                        <div className='mt-12 md:mt-16'>
                            <div className='flex items-center gap-2 mb-5 md:mb-6'>
                                <div
                                    className='w-1 h-6 rounded-full'
                                    style={{
                                        background:
                                            "linear-gradient(180deg, #7F00FF, #FCFA02)",
                                    }}
                                />
                                <h2 className='text-client-font font-semibold text-sm md:text-base tracking-wide uppercase'>
                                    More {catName ? `from ${catName}` : "wallpapers"}
                                </h2>
                            </div>

                            {/* key={slug} → fresh list per wallpaper; excludeSlug hides the current one */}
                            <WallpaperGrid
                                key={slug}
                                slug={catSlug}
                                excludeSlug={slug}
                                emptyTitle='Nothing more here'
                                emptyText='This is the only wallpaper in this category for now.'
                            />
                        </div>
                    )}
                </div>
            </section>

            <WallpaperFooter />
        </div>
    );
};

export default ClientWallpaperView;
